import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-13951">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-13952">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-13953">
              <span className="text-xs font-bold text-indigo-800" data-id="element-13954">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-13955">
            <div className="text-center" data-id="element-13956">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13957">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-13958">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-13959">→</div>
            <div className="text-center" data-id="element-13960">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13961">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-13962">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-13963">
            <table className="w-full text-sm" data-id="element-13964">
              <thead data-id="element-13965">
                <tr className="bg-indigo-100/70" data-id="element-13966">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13967">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13968">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-13969">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-13970">
                      <td className="px-3 py-1.5" data-id="element-13971">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-13972">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-13973">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13974">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13975">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-13976">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13977">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-13978">
            <span className="text-2xl" data-id="element-13979">📝</span>
            <div className="text-left" data-id="element-13980">
              <div className="text-sm font-bold text-indigo-900" data-id="element-13981">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-13982">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-13983">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-13984">
            <div className="text-center" data-id="element-13985">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13986">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-13987">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-13988">→</div>
            <div className="text-center" data-id="element-13989">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13990">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-13991">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-13992">
          <table className="w-full" data-id="element-13993">
            <thead data-id="element-13994">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-13995">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13996">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13997">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-13998">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-13999">
                    <td className="px-4 py-3" data-id="element-14000">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-14001">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-14002">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14003">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-14004">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-14005">
            <div className="flex items-start gap-3" data-id="element-14006">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-14007">
                <span className="text-blue-600 text-lg" data-id="element-14008">🔍</span>
              </div>
              <div data-id="element-14009">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-14010">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14011">
                  De <strong data-id="element-14012">stam</strong> krijg je door <strong data-id="element-14013">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-14014">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-14015">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14016">
              <div className="flex items-start gap-3" data-id="element-14017">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14018">
                  <span className="text-emerald-600 text-lg" data-id="element-14019">📋</span>
                </div>
                <div data-id="element-14020">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14021">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14022">
                    <p data-id="element-14023">
                      <strong data-id="element-14024">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14025">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-14026">
                      <strong data-id="element-14027">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14028">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14029">
                      <strong data-id="element-14030">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14031">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14032">
                      <strong data-id="element-14033">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14034">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14035">
              <div className="flex items-start gap-3" data-id="element-14036">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14037">
                  <span className="text-emerald-600 text-lg" data-id="element-14038">📋</span>
                </div>
                <div data-id="element-14039">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14040">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14041">
                    <p data-id="element-14042">
                      <strong data-id="element-14043">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-14044">
                      <strong data-id="element-14045">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14046">
                      <em data-id="element-14047">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14048">
              <div className="flex items-start gap-3" data-id="element-14049">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14050">
                  <span className="text-emerald-600 text-lg" data-id="element-14051">📋</span>
                </div>
                <div data-id="element-14052">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14053">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14054">
                    <p data-id="element-14055">
                      <strong data-id="element-14056">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-14057">
                      <strong data-id="element-14058">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14059">
                      <em data-id="element-14060">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-14061">
            <div className="flex items-start gap-3" data-id="element-14062">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-14063">
                <span className="text-amber-600 text-lg" data-id="element-14064">💡</span>
              </div>
              <div data-id="element-14065">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-14066">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14067">
                  Bij <strong data-id="element-14068">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-14069">jij loopt</em> wordt{' '}
                  <em data-id="element-14070">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}